import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import ToastContainer from './ToastContainer';
import type { ToastApi, ToastItem, ToastOptions, ToastType } from './toast.types';

// Durees par defaut selon l'etat (ms). L'erreur reste plus longtemps ; les actions rendent persistant.
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3500,
  info: 4000,
  warning: 5000,
  error: 6000,
};

const ToastContext = createContext<ToastApi | null>(null);

let idCounter = 0;
const genId = (): string => `toast_${Date.now().toString(36)}_${(++idCounter).toString(36)}`;

const EXIT_MS = 260; // duree de l'animation de sortie (doit matcher le CSS)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    // No-op si absent : on garde la meme reference -> pas de re-render inutile.
    setToasts((prev) => {
      if (!prev.some((t) => t.id === id)) return prev;
      return prev.map((t) => (t.id === id ? { ...t, exiting: true } : t));
    });
    window.setTimeout(() => {
      setToasts((prev) => {
        const target = prev.find((t) => t.id === id);
        // Re-affiche entre-temps (show a remis exiting=false) -> on ne retire pas.
        if (!target || !target.exiting) return prev;
        return prev.filter((t) => t.id !== id);
      });
    }, EXIT_MS);
  }, []);

  const show = useCallback((opts: ToastOptions): string => {
    const id = opts.id ?? genId();
    const type: ToastType = opts.type ?? 'info';
    const hasActions = !!opts.actions?.length;
    const persistent = opts.persistent ?? opts.duration === 0;
    const duration = opts.duration ?? (persistent || hasActions ? 0 : DEFAULT_DURATIONS[type]);

    const item: ToastItem = {
      id,
      type,
      title: opts.title,
      message: opts.message,
      actions: opts.actions,
      dismissible: opts.dismissible ?? true,
      loading: opts.loading ?? false,
      position: opts.position ?? 'top-right',
      duration,
      exiting: false,
    };

    setToasts((prev) => {
      // Meme id deja present -> mise a jour en place (pas d'empilement).
      if (prev.some((t) => t.id === id)) {
        return prev.map((t) => (t.id === id ? item : t));
      }
      // Append : le plus recent est en dernier dans le DOM -> affiche en haut (container column-reverse).
      return [...prev, item];
    });
    return id;
  }, []);

  const update = useCallback((id: string, opts: ToastOptions): string => show({ ...opts, id }), [show]);

  const api = useMemo<ToastApi>(() => ({
    show,
    update,
    dismiss,
    success: (message, o) => show({ ...o, type: 'success', message }),
    error: (message, o) => show({ ...o, type: 'error', message }),
    warning: (message, o) => show({ ...o, type: 'warning', message }),
    info: (message, o) => show({ ...o, type: 'info', message }),
  }), [show, update, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast doit etre utilise a l\'interieur d\'un <ToastProvider>.');
  }
  return ctx;
};

export default ToastProvider;
